import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * 🏷️ CATIA-Style Flag Marker (Bayrak İşaretleyici)
 * Simple flag marker with text label for marking important points
 */

export class FikoBayrak extends THREE.Group {
    override type = 'FikoBayrak';

    // 🔴 SABİT BOYUT - Kamera zoom'una göre scale ayarlanır
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private pozisyon: THREE.Vector3;
    private metin: string;
    private renk: string;

    private pole: THREE.Line | null = null;
    private flag: THREE.Mesh | null = null;
    private textSprite: THREE.Sprite | null = null;
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    constructor(
        public sahne: FikoSahne,
        pozisyon: THREE.Vector3,
        metin: string = '',
        renk: string = '#FF0000'
    ) {
        super();

        this.pozisyon = pozisyon.clone();
        this.metin = metin;
        this.renk = renk;

        this.canvas = document.createElement('canvas');
        // Varsayılan boyut ayarla (0x0 WebGL hatasına neden olur)
        this.canvas.width = 64;
        this.canvas.height = 32;
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        this.name = 'Bayrak';
        this.renderOrder = 999;

        this.init();

        if (sahne) {
            this.hazirla(sahne);
        }
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.textSprite) {
                this.textSprite.userData.originalScale = new THREE.Vector3().copy(this.textSprite.scale);
                this.kameraYonDegisti();
            }
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { /* Ignored */ }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.textSprite) return;
        const originalScale = this.textSprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;
        this.textSprite.scale.set(originalScale.x * scaleAdjustment, originalScale.y * scaleAdjustment, 1);
        this.textSprite.updateMatrix();
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.textSprite) {
            this.textSprite.userData.originalScale = new THREE.Vector3().copy(this.textSprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        // Pole (vertical line)
        const poleGeometry = new THREE.BufferGeometry().setFromPoints([
            this.pozisyon,
            this.pozisyon.clone().add(new THREE.Vector3(0, 1, 0))
        ]);
        const poleMaterial = new THREE.LineBasicMaterial({
            color: new THREE.Color(this.renk),
            linewidth: 2,
            depthTest: false,
            depthWrite: false
        });
        this.pole = new THREE.Line(poleGeometry, poleMaterial);
        this.add(this.pole);

        // Flag (small triangle)
        const flagGeometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
            0, 1, 0,    // Top of pole
            0.5, 0.8, 0, // Flag tip
            0, 0.6, 0   // Bottom of flag
        ]);
        flagGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        const flagMaterial = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.renk),
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });
        this.flag = new THREE.Mesh(flagGeometry, flagMaterial);
        this.flag.position.copy(this.pozisyon);
        this.add(this.flag);

        if (this.metin) {
            this.createTextSprite();
        }
    }

    private createTextSprite(): void {
        const ctx = this.context;
        ctx.font = 'bold 12px Arial';
        const metrics = ctx.measureText(this.metin);

        const scale = 2;
        const width = Math.pow(2, Math.ceil(Math.log2(metrics.width * scale + 20)));
        const height = Math.pow(2, Math.ceil(Math.log2(24)));

        this.canvas.width = width;
        this.canvas.height = height;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.clearRect(0, 0, width / scale, height / scale);

        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, 0, width / scale, height / scale);

        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.metin, width / scale / 2, height / scale / 2);

        this.texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false
        });

        this.textSprite = new THREE.Sprite(spriteMaterial);
        this.textSprite.position.copy(this.pozisyon.clone().add(new THREE.Vector3(0.5, 1.2, 0)));
        this.textSprite.scale.set(1.5, 0.75, 1);
        this.add(this.textSprite);
    }

    setText(text: string): void {
        this.metin = text;
        if (this.textSprite) this.remove(this.textSprite);
        if (text) this.createTextSprite();
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }
        if (this.pole) {
            this.pole.geometry.dispose();
            (this.pole.material as THREE.Material).dispose();
        }
        if (this.flag) {
            this.flag.geometry.dispose();
            (this.flag.material as THREE.Material).dispose();
        }
        if (this.textSprite) {
            this.textSprite.material.map?.dispose();
            this.textSprite.material.dispose();
        }
        this.texture.dispose();
    }
}
